export type SleepioMedications = {
  prescription: string[];
  otc: string[];
  psychiatric: string[];
  otherPrescription?: string;
  otherOtc?: string;
  otherPsychiatric?: string;
};

export type SleepioSleepDifficultyHistory = {
  duration: 'less_than_1_month' | '1_to_3_months' | '3_to_6_months' | '6_to_12_months' | 'more_than_1_year';
  frequency: 'less_than_1_per_week' | '1_to_2_per_week' | '3_to_4_per_week' | '5_to_6_per_week' | 'every_night';
};

export type SleepioConcurrentTreatmentOption = 'therapist' | 'psychiatrist' | 'neither' | 'prefer_not_to_say';

export type SleepioMHScreenAnswer = 'not_at_all' | 'several_days' | 'more_than_half' | 'nearly_every_day';

export type SleepioPsychosisFlag = 'hearing_voices' | 'seeing_things' | 'unshared_beliefs';

export type SleepioManiaAnswer = 'current_or_recent' | 'past_only' | 'no';

// NEW 2026-06 — prior-diagnosis + stability gate replaces the psychosis/mania/seizure
// symptom stems (see sleepio-integration-manager/briefs/clinic-web-spec.md §3 and the
// 2026-06-05 MH-screener redesign). `adhd` is context-only (never flags); `none` /
// `prefer_not_to_say` are exclusive selections.
export type SleepioPriorDiagnosis =
  | 'bipolar'
  | 'psychotic_spectrum'
  | 'seizure_epilepsy'
  | 'neurodegenerative'
  | 'adhd'
  | 'none'
  | 'prefer_not_to_say';

export type SleepioConditionStability = 'stable_managed' | 'not_stable_or_unsure';

export type SleepioMentalHealthScreen = {
  // DEPRECATED — the web flow no longer sends suicidality / psychosis / mania / seizure.
  // Kept optional so the wire type stays a superset matching nyx + the backend reader.
  suicidality?: SleepioMHScreenAnswer;
  psychosis?: SleepioPsychosisFlag[];
  mania?: SleepioManiaAnswer;
  seizure?: boolean;
  // NEW 2026-06 — the only MH fields web sends.
  priorDiagnoses?: SleepioPriorDiagnosis[];
  conditionStable?: SleepioConditionStability;
};

export type SleepioAsyncRxConsent = {
  acknowledged: boolean;
  version: string;
  at: string;
  // Consent v2.0 (nyx RET-2694) — provenance of what the user saw at consent time.
  disclosureVersion?: string;
  docVersion?: string;
};

export type SleepioIsiAnswer = {
  id: string;
  question: string;
  value: number;
  label?: string;
};

export type SleepioIsiSnapshot = {
  answers: SleepioIsiAnswer[];
  total: number;
  // Wire enum — must match nyx + the backend (≤7 / ≤14 / ≤21 / else).
  category: 'no_clinically_significant' | 'subthreshold' | 'clinical_moderate' | 'clinical_severe';
  takenAt?: string;
};

// OSA follow-up — asked only of high-OSA-risk users (Australia score ≥ 10), mirrors
// nyx RET-2598. Informational only (never gates enrollment); feeds the provider
// charting note + downstream home-sleep-test referral. `derived` is computed
// client-side (deriveSleepioOsaFollowUpState) — the backend consumes it as-is.
export type SleepioOsaDiagnosed = 'yes' | 'no' | 'not_sure';
export type SleepioOsaTreatment = 'cpap' | 'glp1' | 'oral_appliance' | 'not_treated';
export type SleepioOsaFollowUpDerived = 'treated' | 'untreated_or_unknown';
export type SleepioOsaFollowUp = {
  diagnosed?: SleepioOsaDiagnosed;
  treatment?: SleepioOsaTreatment;
  derived: SleepioOsaFollowUpDerived;
};

export type SleepioOsaRisk = {
  // Present only for high-OSA-risk users who answered the follow-up questions.
  followUp?: SleepioOsaFollowUp;
  surveyResponses: {
    bmi: number;
    snoreLoudly: boolean;
    stoppedBreathingDuringSleep: boolean;
    tiredOrFatigued: boolean;
    gasping: boolean;
    headaches: boolean;
    pain?: string;
    dryMouth: boolean;
    highBloodPressure: boolean;
  };
  scores?: {
    // clinic-web's screener computes the Australian OSA score (`australia`); it does
    // NOT compute a STOP-Bang, so `stopBang` is optional and omitted here. (Mobile,
    // which uses STOP-Bang questions, populates it.)
    stopBang?: number;
    australia: number;
  };
  demographics: {
    ageYears?: number;
    gender?: string;
  };
};

export type NewAsyncPatientPayload = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  gender?: string;
  state: string;
  memberId: string;
  payerName?: string;
  sleepDifficultyHistory: SleepioSleepDifficultyHistory;
  medications: SleepioMedications;
  concurrentTreatment: SleepioConcurrentTreatmentOption[];
  mentalHealthScreen: SleepioMentalHealthScreen;
  asyncRxConsent: SleepioAsyncRxConsent;
  isi?: SleepioIsiSnapshot;
  osaRisk?: SleepioOsaRisk;
};

export type NewAsyncPatientRequest = NewAsyncPatientPayload & {
  personId: number;
  timezone: string;
  metadata: {
    surface: 'web';
    userAgent: string;
    version: string;
    referralCode?: string;
  };
};

export type NewAsyncPatientResponse = {
  message?: string;
  intakeId?: number;
  verificationId?: number | null;
  patientId?: number;
  personId?: number;
  mhRedFlag?: boolean;
  error?: string;
};

export type SleepioAllowedPayersByState = Record<string, string[]>;

// Consent flow version. v2.0 (nyx RET-2694) adds the "what happens when I
// submit" disclosure, the amended Rise Health ToS link (RET-2693), and
// billing/data-sharing consent.
export const SLEEPIO_CONSENT_VERSION = '2.0';
// Version of the disclosure text the user saw at consent time — tracked
// independently so a copy change is provable even if the overall consent
// version doesn't move.
export const SLEEPIO_DISCLOSURE_VERSION = '2.0';
// Version of the linked Rise Health ToS / Telehealth Informed Consent /
// Privacy doc (amended in RET-2693). Bump whenever that document changes.
export const SLEEPIO_CONSENT_DOC_VERSION = '2.0';

// ----- Sohar insurance discovery (mirrors nyx services/SleepioRx.ts) -----

export type StartDiscoveryInput = {
  personId: number;
  firstName: string;
  lastName: string;
  dateOfBirth: string; // any parseable form; sent to the API as MM/dd/yyyy
  state: string;
  phone?: string; // forward — backend upserts the patient and nulls omitted fields
  email?: string;
  zipCode?: string; // improves Sohar match confidence
};

export type StartDiscoveryResponse = {
  discoveryId?: string;
  error?: string;
};

// The policy Sohar resolved, present only when discovery completed with found: true.
export type DiscoveryPolicy = {
  payerId?: string; // Sohar/internal numeric id
  payerKey?: string; // backend slug, e.g. 'cigna' — use as the intake payerName
  payerName?: string; // display name
  memberId?: string;
};

// status: 'pending' (also a not-yet-indexed 404) | 'complete' (check `found`) |
//         'cancelled' (poll aborted) | 'error.timed.out' (budget exhausted)
export type DiscoveryCheck = DiscoveryPolicy & {
  status?: string;
  found?: boolean;
  error?: string;
};
